import { useState, useEffect, useCallback, createContext, useContext } from "react";
import api from "../lib/api";

// Default statuses - used as fallback if API fails
const DEFAULT_PROJECT_STATUSES = [
  { id: "proposal_sent", name: "Proposal", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  { id: "contract_negotiation", name: "Negotiation", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  { id: "contract_signed", name: "Signed", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  { id: "sent_to_pool", name: "In Pool", color: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  { id: "in_progress", name: "In Progress", color: "bg-sky-500/10 text-sky-400 border-sky-500/20" },
  { id: "awaiting_client", name: "Awaiting Client", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" },
  { id: "awaiting_payment", name: "Awaiting Payment", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  { id: "completed", name: "Completed", color: "bg-green-500/10 text-green-400 border-green-500/20" },
];

const DEFAULT_PAYMENT_STATUSES = [
  { id: "pending", name: "Pending", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  { id: "approved", name: "Approved", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  { id: "paid", name: "Paid", color: "bg-green-500/10 text-green-400 border-green-500/20" },
];

const DEFAULT_REQUEST_STATUSES = [
  { id: "pending", name: "Pending", color: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20" },
  { id: "responded", name: "Responded", color: "bg-green-500/10 text-green-400 border-green-500/20" },
];

// Context for statuses
const StatusesContext = createContext(null);

export const StatusesProvider = ({ children }) => {
  const [projectStatuses, setProjectStatuses] = useState(DEFAULT_PROJECT_STATUSES);
  const [paymentStatuses, setPaymentStatuses] = useState(DEFAULT_PAYMENT_STATUSES);
  const [requestStatuses, setRequestStatuses] = useState(DEFAULT_REQUEST_STATUSES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatuses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/settings/statuses/public");
      const data = response.data;
      
      if (data.project_statuses?.length > 0) {
        setProjectStatuses(data.project_statuses);
      }
      if (data.payment_statuses?.length > 0) {
        setPaymentStatuses(data.payment_statuses);
      }
      if (data.request_statuses?.length > 0) {
        setRequestStatuses(data.request_statuses);
      }
      setError(null);
    } catch (err) {
      console.warn("Using default status configurations:", err.message);
      setError(err);
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Helper to get status by ID

  const getProjectStatus = useCallback((id) => {
    return projectStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  }, [projectStatuses]);

  const getPaymentStatus = useCallback((id) => {
    return paymentStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  }, [paymentStatuses]);

  const getRequestStatus = useCallback((id) => {
    return requestStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  }, [requestStatuses]);

  return (
    <StatusesContext.Provider 
      value={{
        projectStatuses,
        paymentStatuses,
        requestStatuses,
        loading,
        error,
        refetch: fetchStatuses,
        getProjectStatus,
        getPaymentStatus,
        getRequestStatus,
      }}
    >
      {children}
    </StatusesContext.Provider>
  );
};

// Hook to use statuses
export const useStatuses = () => {
  const context = useContext(StatusesContext);
  if (!context) {
    throw new Error("useStatuses must be used within a StatusesProvider");
  }
  return context;
};

// Standalone hook for components outside provider (e.g., in pages)
export const useStatusesStandalone = () => {
  const [projectStatuses, setProjectStatuses] = useState(DEFAULT_PROJECT_STATUSES);
  const [paymentStatuses, setPaymentStatuses] = useState(DEFAULT_PAYMENT_STATUSES);
  const [requestStatuses, setRequestStatuses] = useState(DEFAULT_REQUEST_STATUSES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await api.get("/settings/statuses/public");
        const data = response.data;
        
        if (data.project_statuses?.length > 0) setProjectStatuses(data.project_statuses);
        if (data.payment_statuses?.length > 0) setPaymentStatuses(data.payment_statuses);
        if (data.request_statuses?.length > 0) setRequestStatuses(data.request_statuses);
      } catch (err) {
        console.warn("Using default status configurations");
      } finally {
        setLoading(false);
      }
    };
    fetchStatuses();
  }, []);

  const getProjectStatus = (id) => projectStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  const getPaymentStatus = (id) => paymentStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  const getRequestStatus = (id) => requestStatuses.find(s => s.id === id) || { id, name: id, color: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };

  return {
    projectStatuses,
    paymentStatuses,
    requestStatuses,
    loading,
    getProjectStatus,
    getPaymentStatus,
    getRequestStatus,
  };
};

export default useStatuses;
